import i18n from "@/i18n";
import { AddressCompanyType, RegisterUser } from "@/types";

export type ValidateReturn<T extends object> =
  | {
      isValid: true;
      data: T;
    }
  | {
      isValid: false;
      errors: ValidateError<T>;
    };

export type ValidateError<T> = {
  [key in keyof T]?: string;
};

// #region validateEmail
export function validateEmail(email: string): boolean {
  // validate email
  const emailRegex = /^[^\s@]+@([^\s@.]+\.)+[^\s@.]+$/;
  return emailRegex.test(email);
}
// #endregion validateEmail

// #region validateRegistration
export function validateRegistration(
  registrationInfo: RegisterUser
): ValidateReturn<RegisterUser> {
  //	Full Name (Mandatory, Text, Max 100 characters)
  //	Email Address (Mandatory, Unique, Text, Max 150 characters)
  //	Password (Mandatory, Text, Min 8 characters, 1 special character, Max 50 characters)
  //	Mobile Number (Optional, Numeric, 10-15 digits)
  //	Company Name (Optional, Text, Max 100 characters)
  // Address Line 1 (Mandatory, Text)
  // Address Line 2 (Optional, Text)
  // Suburb (Mandatory, Text)
  // Town (Mandatory, Text)
  // Postal Code (Mandatory, Numeric, 4-10 digits)
  //	Authentication Method (Dropdown - Google, Office 365, Facebook, LinkedIn)
  const errors: ValidateError<RegisterUser> = {};

  // Full Name
  const trimmedFullName = registrationInfo.fullName?.trim() || "";
  if (!trimmedFullName) {
    errors.fullName = i18n.t("Full name is required");
  } else if (trimmedFullName.length > 100) {
    errors.fullName = i18n.t("Full name max length is 100 characters");
  }

  // Email
  const trimmedEmail = registrationInfo.email?.trim() || "";
  if (!trimmedEmail) {
    errors.email = i18n.t("Email is required");
  } else if (trimmedEmail.length > 150) {
    errors.email = i18n.t("Email max length 150 characters");
  } else if (!validateEmail(trimmedEmail)) {
    errors.email = i18n.t("Invalid email format");
  }

  // Password
  if (!registrationInfo.password) {
    errors.password = i18n.t("Password is required");
  } else if (registrationInfo.password.length < 8) {
    errors.password = i18n.t("Password must be at least 8 characters");
  } else if (registrationInfo.password.length > 50) {
    errors.password = i18n.t("Password max length 50 characters");
  } else if (!/[^A-Za-z0-9]/.test(registrationInfo.password)) {
    errors.password = i18n.t(
      "Password must contain at least one special character"
    );
  }

  // Company Name
  // const trimmedCompanyName = registrationInfo.companyName?.trim() || "";
  // if (trimmedCompanyName && trimmedCompanyName.length > 100) {
  //   errors.companyName = i18n.t("Company name max length is 100 characters");
  // }

  // Mobile Number
  const trimmedMobileNumber = registrationInfo.mobileNumber?.trim() || "";

  if (trimmedMobileNumber && !/^\d+$/.test(trimmedMobileNumber)) {
    errors.mobileNumber = i18n.t("Mobile number must only contain numbers");

    if (
      trimmedMobileNumber &&
      (trimmedMobileNumber.length < 10 || trimmedMobileNumber.length >= 15)
    ) {
      errors.mobileNumber = i18n.t("Mobile number must be 10-15 digits");
    }
  }

  // Address Line 1
  // if (!registrationInfo.addressLine1) {
  //   errors.addressLine1 = i18n.t("Address Line 1 is required");
  // }
  // const trimmedAddressLine1 = registrationInfo.addressLine1.trim();

  // if (trimmedAddressLine1.length === 0) {
  //   errors.addressLine1 = i18n.t("Address Line 1 is required");
  // } else if (trimmedAddressLine1.length > 100) {
  //   errors.addressLine1 = i18n.t("Address Line 1 max length is 100 characters");
  // }

  // // Address Line 2
  // const trimmedAddressLine2 = registrationInfo.addressLine2?.trim() || "";

  // if (trimmedAddressLine2 && trimmedAddressLine2.length > 100) {
  //   errors.addressLine2 = i18n.t("Address Line 2 max length is 100 characters");
  // }

  // // Suburb
  // if (!registrationInfo.suburb) {
  //   errors.suburb = i18n.t("Suburb is required");
  // }
  // const trimmedSuburb = registrationInfo.suburb.trim();

  // if (trimmedSuburb.length === 0) {
  //   errors.suburb = i18n.t("Suburb is required");
  // } else if (trimmedSuburb.length > 100) {
  //   errors.suburb = i18n.t("Suburb max length is 100 characters");
  // }

  // // Town
  // if (!registrationInfo.town) {
  //   errors.town = i18n.t("Town is required");
  // }
  // const trimmedTown = registrationInfo.town.trim();

  // if (trimmedTown.length === 0) {
  //   errors.town = i18n.t("Town is required");
  // } else if (trimmedTown.length > 100) {
  //   errors.town = i18n.t("Town max length is 100 characters");
  // }

  // // Postal Code
  // if (!registrationInfo.postalCode) {
  //   errors.postalCode = i18n.t("Postal code is required");
  // }
  // const trimmedPostalCode = registrationInfo.postalCode.trim();

  // if (trimmedPostalCode.length === 0) {
  //   errors.postalCode = i18n.t("Postal code is required");
  // } else if (!/^\d{4,10}$/.test(trimmedPostalCode)) {
  //   errors.postalCode = i18n.t("Postal code must be 4-10 digits");
  // }

  // if (!AUTH_METHODS.includes(registrationInfo.authMethod)) {
  //   errors.authMethod = i18n.t("Invalid authentication method");
  // }

  if (Object.keys(errors).length > 0) {
    console.error("Registration validation failed", errors);
    return { isValid: false, errors };
  }

  return {
    isValid: true,
    data: {
      fullName: trimmedFullName,
      email: trimmedEmail,
      password: registrationInfo.password,
      mobileNumber: trimmedMobileNumber,
      // authMethod: registrationInfo.authMethod,
    },
  };
}
// #endregion validateRegistration

// #region validateCompanyAddress

export function validateCompanyAddress(
  companyAddressInfo: AddressCompanyType
): { valid: boolean; errors?: AddressCompanyType } {
  const errors: AddressCompanyType = {
    companyName: "",
    address: {
      addressLine1: "",
      addressLine2: "",
      suburb: "",
      town: "",
      postalCode: "",
    },
  };

  let errorFound = false;

  if (companyAddressInfo.companyName) {
    if (companyAddressInfo.companyName.trim().length > 100) {
      errors.companyName = i18n.t("Company name max length is 100 characters");
      errorFound = true;
    }
  }

  if (!companyAddressInfo.address.addressLine1) {
    errors.address.addressLine1 = i18n.t("Address Line 1 is required");
    errorFound = true;
  } else if (companyAddressInfo.address.addressLine1.trim().length > 100) {
    errors.address.addressLine1 = i18n.t(
      "Address Line 1 max length is 100 characters"
    );
    errorFound = true;
  }

  if (companyAddressInfo.address.addressLine2) {
    if (companyAddressInfo.address.addressLine2.trim().length > 100) {
      errors.address.addressLine2 = i18n.t(
        "Address Line 2 max length is 100 characters"
      );
      errorFound = true;
    }
  }

  if (!companyAddressInfo.address.suburb) {
    errors.address.suburb = i18n.t("Suburb is required");
    errorFound = true;
  } else if (companyAddressInfo.address.suburb.trim().length > 100) {
    errors.address.suburb = i18n.t("Suburb max length is 100 characters");
    errorFound = true;
  }

  if (!companyAddressInfo.address.town) {
    errors.address.town = i18n.t("Town is required");
    errorFound = true;
  } else if (companyAddressInfo.address.town.trim().length > 100) {
    errors.address.town = i18n.t("Town max length is 100 characters");
    errorFound = true;
  }

  if (!companyAddressInfo.address.postalCode) {
    errors.address.postalCode = i18n.t("Postal code is required");
    errorFound = true;
  } else if (!/^\d{4,10}$/.test(companyAddressInfo.address.postalCode.trim())) {
    errors.address.postalCode = i18n.t("Postal code must be 4-10 digits");
    errorFound = true;
  }

  if (!errorFound) {
    return { valid: true };
  } else {
    return { valid: false, errors };
  }
}

// #endregion validateRegistration

export const confirmPasswordValidation = (
  password: string,
  confirmPassword: string
): boolean => {
  if (!password || !confirmPassword) {
    return false;
  }

  const normalizedPassword = password.trim().normalize();
  const normalizedConfirmPassword = confirmPassword.trim().normalize();

  if (normalizedPassword !== normalizedConfirmPassword) {
    return false;
  }
  return true;
};

// #endregion confirmPassword

export const validatePassword = (password: string): boolean => {
  // Enforce min 8, max 128, at least 1 uppercase, 1 special char
  return (
    password.length >= 8 &&
    password.length <= 128 &&
    /[A-Z]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
};
